/**
 * Device detection utilities for optimizing user experience
 * across different device types and capabilities
 */

import React from 'react';

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isTouchDevice: boolean;
  screenSize: {
    width: number;
    height: number;
  };
  userAgent: string;
  supportsDownload: boolean;
}

/**
 * Detects if the current device is mobile based on multiple criteria
 */
export function detectDevice(): DeviceInfo {
  const userAgent = navigator.userAgent.toLowerCase();
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;
  
  // Mobile user agent patterns
  const mobilePattern = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  const tabletPattern = /ipad|android(?=.*tablet)|tablet/i;
  
  // Touch device detection
  const isTouchDevice = 'ontouchstart' in window || 
                       navigator.maxTouchPoints > 0 || 
                       // @ts-ignore - for IE compatibility
                       navigator.msMaxTouchPoints > 0;
  
  // Screen size based detection (mobile < 768px, tablet 768-1024px)
  const isMobileByScreen = screenWidth < 768;
  const isTabletByScreen = screenWidth >= 768 && screenWidth <= 1024;
  
  // Combine user agent and screen size detection
  const isMobileUA = mobilePattern.test(userAgent) && !tabletPattern.test(userAgent);
  const isTabletUA = tabletPattern.test(userAgent);
  
  const isMobile = isMobileUA || (isMobileByScreen && isTouchDevice);
  const isTablet = isTabletUA || (isTabletByScreen && isTouchDevice && !isMobile);
  const isDesktop = !isMobile && !isTablet;
  
  // Download support detection
  const supportsDownload = 'download' in document.createElement('a');
  
  return {
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
    screenSize: {
      width: screenWidth,
      height: screenHeight
    },
    userAgent,
    supportsDownload
  };
}

/**
 * React hook for device detection with reactive updates
 */
export function useDeviceDetection() {
  const [deviceInfo, setDeviceInfo] = React.useState<DeviceInfo>(() => detectDevice());
  
  React.useEffect(() => {
    const handleResize = () => {
      setDeviceInfo(detectDevice());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return deviceInfo;
}

/**
 * Checks if the device prefers mobile-optimized downloads
 * (individual files instead of zip archives)
 */
export function prefersMobileDownload(): boolean {
  const device = detectDevice();
  
  // Mobile devices prefer individual downloads for better UX
  if (device.isMobile) return true;
  
  // Tablets with touch may also prefer individual downloads
  if (device.isTablet && device.isTouchDevice) return true;
  
  // Small screens even on desktop may benefit from individual downloads
  if (device.screenSize.width < 768) return true;
  
  return false;
}

/**
 * Gets the optimal download strategy based on device capabilities
 */
export function getDownloadStrategy(): 'zip' | 'individual' | 'sequential' {
  const device = detectDevice();
  
  // Desktop with good download support: use zip
  if (device.isDesktop && device.supportsDownload) {
    return 'zip';
  }
  
  // Mobile or tablets: prefer individual downloads
  if (device.isMobile || device.isTablet) {
    return 'individual';
  }
  
  // Fallback for older browsers: sequential individual downloads
  if (!device.supportsDownload) {
    return 'sequential';
  }
  
  return 'zip';
}

/**
 * Creates a delay between downloads to avoid overwhelming mobile browsers
 */
export function getDownloadDelay(): number {
  const device = detectDevice();
  
  if (device.isMobile) return 1000; // 1 second delay on mobile
  if (device.isTablet) return 500;  // 0.5 second delay on tablet
  return 200; // 0.2 second delay on desktop
}